<script lang="ts" setup>
import { computed, useCssModule } from 'vue';
import type { Slot } from 'vue';
import { hasSlotContent } from '../../utils';
type UiDataContainerVariant = 'default' | 'table';
const props = withDefaults(
  defineProps<{
    variant?: UiDataContainerVariant;
  }>(),
  {
    variant: 'default',
  },
);
const slots = defineSlots<{
  default?: Slot;
  headerStart?: Slot;
  headerEnd?: Slot;
  footerStart?: Slot;
  footerEnd?: Slot;
}>();
const hasFooter = computed(() => {
  return hasSlotContent(slots.footerStart) || hasSlotContent(slots.footerEnd);
});
const styles = useCssModule();
const rootClasses = computed(() => {
  return [
    styles.root,
    props.variant === 'table' ? styles.rootTable : null,
    !hasFooter.value ? styles.rootNoFooter : null,
  ];
});
</script>

<template>
  <div :class="rootClasses">
    <div
      v-if="hasSlotContent($slots.headerStart) || hasSlotContent($slots.headerEnd)"
      :class="$style.header"
    >
      <div :class="$style.headerStart">
        <slot name="headerStart" />
      </div>
      <div :class="$style.headerEnd">
        <slot name="headerEnd" />
      </div>
    </div>
    <div :class="$style.body">
      <slot />
    </div>
    <div
      v-if="hasSlotContent($slots.footerStart) || hasSlotContent($slots.footerEnd)"
      :class="$style.footer"
    >
      <div :class="$style.footerStart">
        <slot name="footerStart" />
      </div>
      <div :class="$style.footerEnd">
        <slot name="footerEnd" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.root {
  display: flex;
  flex-direction: column;
  background: var(--ui2-background-primary);
  border-radius: 10px;
  overflow: hidden;
  overflow-anchor: none;
  display: flex;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  border: 1px solid #f0f1f4;
  padding: 8px;
}

.rootNoFooter {
  padding-bottom: 0;
}

.rootTable {
  padding: 0;
}

.rootTable .header,
.rootTable .footer {
  padding: 0;
  min-height: 0;
}

.rootTable .headerStart:not(:empty),
.rootTable .headerEnd:not(:empty),
.rootTable .footerStart:not(:empty),
.rootTable .footerEnd:not(:empty) {
  padding: 8px;
}

.rootTable .body {
  padding: 8px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.header {
  padding: 0 0 8px;
  border-bottom: 1px solid var(--ui2-stroke-tertiary);
}

.body {
  flex: 1 1 0%;
  overflow: auto;
  box-sizing: border-box;
}

.footer {
  padding: 20px 8px 2px;
}

.header,
.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
  flex: 0 0 auto;
  box-sizing: border-box;
}

.headerStart,
.footerStart {
  flex-basis: 0;
  flex-grow: 1;
  max-width: 100%;
}
</style>
